import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { sanitizeHTML } from '../../utils/sanitize';

// FAQ items data
const faqItems = [
  {
    question: "Qui est Jeinar ?",
    isLink: true,
    linkTo: "/valouse/story/jeinar"
  },
  { question: "Faut-il être avancé·e spirituellement ?", answer: "Non. COCON ne demande aucune connaissance préalable. Ce qui compte n'est pas ton niveau, mais ta disponibilité intérieure. Tu viens tel·le que tu es." },
  { question: "Et si je me sens submergé·e ?", answer: "Alors tu seras accompagné·e.\n\nLes 7 coconeuses sont là pour cela.\nPas pour enseigner.\nPas pour diriger.\nMais pour soutenir.\n\nCOCON peut toucher profond.\nMais la profondeur est contenue.\n\nTu n'es jamais exposé·e.\nTu es entouré·e." },
  { question: "Est-ce une retraite \"perchée\" ?", answer: "Non. COCON est spirituel et incarné. Profond, mais ancré dans le corps et la réalité. Ici, on vit. On ressent. On intègre." },
  { question: "Est-ce réservé aux femmes ?", answer: "Non. COCON est mixte. La richesse du cercle vient de la diversité des présences." },
  { question: "Puis-je venir en couple ?", answer: "Oui. Certaines personnes choisissent de vivre COCON en couple. Chacun·e traverse son propre cheminement, dans un cadre commun. Le travail est individuel. Le lien peut, lui aussi, se transformer." },
  { question: "Comment fonctionne le paiement échelonné ?", answer: "Pour faciliter l'accès au cycle COCON, un paiement échelonné sans frais est proposé pour chaque formule.\n\nSelon la date d'inscription et la formule choisie (immersion seule, cycle partiel ou expérience complète), l'échelonnement peut aller jusqu'à 9 mensualités sans frais.\n\nLe principe est simple : les mensualités sont réparties de manière cohérente avec le calendrier du cycle et la date de début de la retraite.\n\nLes modalités exactes sont proposées au moment de l'inscription afin d'offrir une solution adaptée à chaque participant·e." },
  { question: "Que comprend le prix de la retraite ?", answer: "Le tarif comprend l'ensemble du programme COCON correspondant à la formule choisie.\n\nPour l'immersion à Valouse, cela inclut :\n– les pratiques et accompagnements pendant la retraite\n– l'hébergement au Hameau de Valouse\n– les repas partagés sur place\n– l'accès aux espaces du domaine pendant toute la durée du séjour\n\nLes détails précis sont indiqués au moment de l'inscription afin que chacun·e sache exactement ce qui est inclus dans son expérience." },
  { question: "Le transport jusqu'à Valouse est-il inclus ?", answer: "Le transport jusqu'au Hameau de Valouse n'est pas inclus dans le tarif de la retraite.\n\nAprès l'inscription, les participant·e·s reçoivent toutes les informations nécessaires pour organiser leur arrivée simplement (gares proches, accès routier, suggestions de covoiturage entre participant·e·s si souhaité).\n\nL'objectif est que chacun·e puisse rejoindre le lieu avec fluidité et sérénité." },
  { question: "Combien de participant·e·s composent le cercle ?", answer: "Le cercle est volontairement limité à 20 participant·e·s maximum.\n\nCe format permet de préserver :\n– la qualité du lien\n– la profondeur des pratiques\n– l'attention portée à chaque personne\n\nCOCON n'est pas conçu comme un grand événement, mais comme un espace de transformation intime et vivant." },
  { question: "Quelle est la durée de COCON ?", answer: `COCON se vit en trois temps — préparation, immersion, intégration — qui forment un seul mouvement.

L'immersion au Hameau de Valouse a lieu du 7 au 11 septembre 2026 (5 jours, 4 nuits).

Le cycle complet s'étend du 25 juillet au 31 octobre 2026.

Tu peux choisir d'entrer à différents moments du processus.
Chaque format est une porte d'entrée.` },

  /* ═══ CONSERVATION TEMPORAIRE — ancien contenu FAQ "Quelle est la durée de COCON ?" ═══
   * L'ancien texte détaillait les 4 formules individuellement avec dates et descriptions.
   * Il a été simplifié car les dates et le cycle sont maintenant présentés dans la section "Le cycle COCON".
   * ═══ FIN CONSERVATION TEMPORAIRE ═══ */
  { question: "Et si je ne suis pas sûr·e ?", answer: "Alors attends." }
];

const ValouseFaqSection = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <section id="faq" className="py-16 sm:py-20 px-6 bg-[#E8D2B5]/50">
      <div className="max-w-3xl mx-auto space-y-8">
        
        {/* Premium micro-heading structure */}
        <div className="text-center space-y-3">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059]">
            Vos questions
          </p>
          <h2 
            className="font-serif text-2xl md:text-3xl text-[#3F2A24]"
            style={{ textWrap: 'balance' }}
          >
            Questions Fréquentes
          </h2>
          <p 
            className="font-serif text-sm text-[#3F2A24]/70 italic"
            style={{ textWrap: 'balance' }}
          >
            Ce que les autres ont demandé avant de venir
          </p>
        </div>
        
        <div className="space-y-4">
          {faqItems.map((item, index) => {
            // Link-only item (Qui est Jeinar?)
            if (item.isLink) {
              return (
                <div key={`faq-${item.question}`} id="faq-jeinar" className="rounded-2xl overflow-hidden">
                  <Link
                    to={item.linkTo}
                    className="w-full px-6 py-5 flex justify-between items-center text-left bg-[#F3E5CF] text-[#3F2A24] hover:bg-[#8B7D5E] hover:text-[#F3E5CF] transition-colors min-h-[56px] group"
                    data-testid={`faq-link-${index}`}
                  >
                    <span className="font-medium text-base sm:text-lg" style={{ textWrap: 'balance' }}>{item.question}</span>
                    <ChevronDown className="w-5 h-5 flex-shrink-0 ml-4 opacity-60 group-hover:opacity-100 transition-opacity -rotate-90" />
                  </Link>
                </div>
              );
            }

            // Regular expandable item — inverted: light bg default, dark on open
            return (
              <div key={`faq-${item.question}`} className="rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className={`w-full px-6 py-5 flex justify-between items-center text-left transition-colors min-h-[56px] ${
                    openFaq === index 
                      ? "bg-[#8B7D5E] text-[#F3E5CF]" 
                      : "bg-[#F3E5CF] text-[#3F2A24] hover:bg-[#8B7D5E] hover:text-[#F3E5CF]"
                  }`}
                  data-testid={`faq-btn-${index}`}
                >
                  <span className="font-medium text-base sm:text-lg" style={{ textWrap: 'balance' }}>{item.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 ml-4 transition-transform ${
                      openFaq === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 py-5 bg-[#F3E5CF] text-[#3F2A24] whitespace-pre-line leading-[1.8] text-sm sm:text-base">
                    {item.html ? (
                      <div dangerouslySetInnerHTML={sanitizeHTML(item.answer)} />
                    ) : (
                      item.answer
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ValouseFaqSection;
